import {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams
} from 'react-router-dom';
import {
  BackButton,
  ButtonWithIcon,
  ProgressIndicator
} from '@/components/atoms';
import {getPerfectStoreStepConfig} from '@/utils/perfectStoreStepConfig';
import {Accordion} from '@/components/molecules/Accordion';
import {AccordionItem} from '@/components/molecules/AccordionItem';
import {PageHeader, SaveCancelFooter} from '@/components/molecules';
import {
  PerfectStoreSetupReview,
  PerfectStoreDisplayReview
} from '@/containers/PerfectStore/PerfectStoreReview';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  setPerfectStoreFormData,
  setPerfectStoreMeta
} from '@/store/slices/perfectStoreSlice';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {ROUTES} from '@/utils/constants/routesConstants';
import {
  getPerfectStoreCancelModal,
  isPerfectStoreDisplayInvalid,
  mapPerfectStoreApiToFormData
} from '@/pages/PerfectStoreUtils';
import {CREATE_PERFECT_STORE_TITLE} from '@/utils/constants/perfectStoreConstants';
import {usePerfectStoreReviewActions} from '@/hooks/usePerfectStoreReviewActions';
import {getPerfectStoreDetail} from '@/services/perfectStore';
import {Constants} from '@/utils/constants/commonConstants';
import {logger} from '@/helpers/logger';
import {isHistoryBack} from '@/helpers/navigationHistory';
import '@/containers/PerfectStore/PerfectStoreReview/perfectStoreReview.scss';

const stepConfig = getPerfectStoreStepConfig();

const EditSectionButton = ({step, onEdit}) => (
  <ButtonWithIcon
    iconLeading='edit'
    handleButton={(event) => {
      event?.stopPropagation();
      onEdit(step);
    }}
    uiSchema={{
      title: 'Edit Section',
      variant: 'tertiary',
      size: 'small',
      isDark: true
    }}
  />
);

EditSectionButton.propTypes = {
  step: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired
};

export const PerfectStoreReview = () => {
  const {id} = useParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const disableDraftFromState = location?.state?.disableDraft ?? false;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formData = useAppSelector((state) => state?.perfectStore?.formData);
  const perfectStoreStatus = useAppSelector(
    (state) => state?.perfectStore?.status
  );
  const storedId = useAppSelector((state) => state?.perfectStore?.id);
  const countryCode = useAppSelector(selectCountryCode);
  const commonLanguages = useAppSelector((state) => state.common?.languages);

  const fromWizard = location?.state?.fromWizard;
  const skipDetailFetch =
    Boolean(fromWizard) ||
    (isHistoryBack(navigationType, location.key) &&
      storedId != null &&
      id != null &&
      String(storedId) === String(id));

  const [isLoadingDetail, setIsLoadingDetail] = useState(
    !!id && !skipDetailFetch
  );

  const isPublishedDetail =
    !!id &&
    String(perfectStoreStatus || formData?.status || '').toLowerCase() ===
      Constants.Published.toLowerCase();
  const disableDraft = disableDraftFromState || isPublishedDetail;

  useEffect(() => {
    if (!id || skipDetailFetch) {
      setIsLoadingDetail(false);
      return undefined;
    }

    let cancelled = false;

    const loadPerfectStoreDetail = async () => {
      setIsLoadingDetail(true);
      try {
        const response = await getPerfectStoreDetail(id);
        const detail =
          response?.data?.content?.[0] ??
          response?.data ??
          response?.content?.[0] ??
          response;

        if (cancelled || !detail || typeof detail !== 'object') {
          return;
        }

        dispatch(setPerfectStoreFormData(mapPerfectStoreApiToFormData(detail)));
        dispatch(
          setPerfectStoreMeta({
            id: detail.id ?? id,
            uuid: detail.uuid ?? null,
            status: detail.status ?? null
          })
        );
      } catch (error) {
        logger.error('Error loading Perfect Store detail:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingDetail(false);
        }
      }
    };

    loadPerfectStoreDetail();

    return () => {
      cancelled = true;
    };
  }, [dispatch, id, skipDetailFetch]);

  const languages = useMemo(
    () => COUNTRY_LANGUAGES_CODES[countryCode] || commonLanguages || [],
    [countryCode, commonLanguages]
  );

  const getNavigationState = (returnTo) => ({
    isEdit: true,
    disableDraft,
    fromWizard: true,
    ...(location.pathname ? {from: location.pathname} : {}),
    ...(returnTo && {returnTo})
  });

  const handleStepClick = (step) => {
    const stepRoutes = {
      1: ROUTES.PERFECT_STORE_ADD,
      2: ROUTES.PERFECT_STORE_DISPLAY
    };
    if (stepRoutes[step]) {
      navigate(stepRoutes[step], {
        state: getNavigationState(step === 2 ? 'review' : undefined)
      });
    }
  };

  const {
    isCancel,
    handleCancel,
    onClickPrimaryButton,
    handleDraftAction,
    handlePublishAction,
    publishDisabled
  } = usePerfectStoreReviewActions({
    formData,
    languages,
    isDisplayInvalid: isPerfectStoreDisplayInvalid
  });

  return (
    <>
      {isLoadingDetail ? (
        <p data-testid='perfect-store-review-loading'>Loading...</p>
      ) : (
        <div
          className='perfect-store-detail-page'
          data-testid='perfect-store-detail-page'>
          {/* ── Back button ── */}
          <BackButton id='perfect-store-review-back' />

          {/* ── Page title ── */}
          <PageHeader
            title={id ? formData?.name : CREATE_PERFECT_STORE_TITLE}
          />

          {/* ── Progress indicator (wizard creation or edit-section flow) ── */}
          {(!id || fromWizard) && (
            <ProgressIndicator
              currentStep={3}
              completed={false}
              orientation='horizontal'
              steps={stepConfig.steps}
              titles={stepConfig.titles}
              className='mb-6'
              onStepClick={handleStepClick}
            />
          )}

          {/* ── Requirements accordion ── */}
          <div
            className='perfect-store-review flex flex-col gap-4 pt-4'
            data-testid='perfect-store-setup-review-wrapper'>
            <Accordion
              content={
                <AccordionItem
                  expanded
                  label='Setup'
                  rightAccessoryChild={
                    <EditSectionButton step={1} onEdit={handleStepClick} />
                  }
                  content={<PerfectStoreSetupReview formData={formData} />}
                />
              }
            />
            <Accordion
              content={
                <AccordionItem
                  expanded
                  label='Display'
                  rightAccessoryChild={
                    <EditSectionButton step={2} onEdit={handleStepClick} />
                  }
                  content={
                    <PerfectStoreDisplayReview
                      formData={formData}
                      languages={languages}
                    />
                  }
                />
              }
            />
          </div>

          {/* ── Footer ── */}
          <SaveCancelFooter
            FormError={publishDisabled}
            handleCancel={handleCancel}
            draftAction={handleDraftAction}
            nextAction={handlePublishAction}
            hideSaveDraft={false}
            disableDraft={disableDraft}
            isReview
          />
        </div>
      )}
      {isCancel &&
        getPerfectStoreCancelModal({
          show: isCancel,
          onConfirm: onClickPrimaryButton,
          onClose: handleCancel
        })}
    </>
  );
};
